import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn } from "lucide-react";
import { toast } from "sonner";
import { entrarConCodigo } from "@/lib/api";

/** Entrar en una clase con su código. `/entrar` a mano, `/entrar/ABC123` desde el QR.
 *
 * POR QUE UNA PAGINA Y NO UN CAMPO EN UN PANEL. Lo que se proyecta en la pizarra es una
 * dirección, y una dirección tiene que llevar a algún sitio. Con el código en la ruta, el
 * QR entra solo: se escanea y ya está dentro. Sin él, la misma página pide que lo tecleen,
 * que es lo que hace falta en octubre cuando llega uno nuevo y nadie proyecta nada.
 *
 * `ProtectedRoute` la envuelve, así que quien no tenga sesión pasa antes por el login y
 * vuelve aquí. Y si su cuenta es de aula recién dada de alta, antes cambiará su contraseña:
 * esa puerta también está en `ProtectedRoute` y se atraviesan en orden.
 */
export default function Entrar() {
  const { codigo: codigoUrl } = useParams();
  const navigate = useNavigate();
  const [codigo, setCodigo] = useState(codigoUrl || "");
  const [entrando, setEntrando] = useState(false);
  const yaProbado = useRef(false);

  const entra = async (cual) => {
    if (!cual?.trim() || entrando) return;
    setEntrando(true);
    try {
      const clase = await entrarConCodigo(cual);
      toast.success(`Ya estás en ${clase.name}.`);
      navigate("/mundo", { replace: true });
    } catch {
      toast.error("Ese código no abre ninguna clase.");
      setEntrando(false);
    }
  };

  // Con el codigo en la direccion no se pide nada: se entra. Y UNA sola vez, que si no un
  // re-render vuelve a llamar y el alumno ve dos avisos de lo mismo.
  //
  // EL AVISO DE `entra` SE CALLA A PROPOSITO, y con motivo escrito. `entra` se vuelve a crear
  // en cada render -depende de `entrando`-, asi que meterla en las dependencias haria correr
  // este efecto en cada render. Quien garantiza la unica vez es `yaProbado`, que es un `ref` y
  // no cuenta como dependencia. Poner la funcion aqui seria obedecer a la regla y romper lo
  // que la regla protege.
  //
  // Y esto no es una manía: hasta hoy este aviso hacia **fallar el build de Netlify**, porque
  // alli `CI=true` convierte los avisos en errores. Se arregla el aviso en vez de apagar esa
  // conversion, que es lo que convierte el build en un portero mas.
  useEffect(() => {
    if (codigoUrl && !yaProbado.current) {
      yaProbado.current = true;
      entra(codigoUrl);
    }
    // La marca va aqui y no encima del `useEffect`: `exhaustive-deps` se queja en la linea del
    // array, asi que ahi es donde hay que callarla. Es como lo hace ya `CuentaAtras.jsx`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigoUrl]);

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-white min-h-[calc(100vh-4rem)] dvh:min-h-[calc(100svh-4rem)] flex items-center justify-center px-6"
      data-testid="page-entrar"
    >
      <form
        onSubmit={(e) => { e.preventDefault(); entra(codigo); }}
        className="w-full max-w-[420px] text-center"
      >
        <div className="font-mono-alpha text-[10px] uppercase tracking-widest text-alpha-black/40 mb-4 inline-flex items-center gap-2">
          <LogIn className="w-3.5 h-3.5" /> Entrar en una clase
        </div>
        <h1 className="font-serif-display text-4xl leading-tight mb-3">
          {entrando ? "Entrando…" : "El código de tu clase"}
        </h1>
        <p className="text-alpha-black/60 mb-8">
          Seis caracteres, los que haya puesto tu profesor en la pizarra.
        </p>
        <input
          value={codigo}
          onChange={(e) => setCodigo(e.target.value.toUpperCase())}
          maxLength={12}
          autoFocus={!codigoUrl}
          placeholder="ABC123"
          data-testid="entrar-codigo"
          className="rounded-caja w-full border border-alpha-black/20 focus:border-alpha-black outline-none px-3 py-3 mb-4 text-center font-mono-alpha text-2xl tracking-[0.3em] uppercase"
        />
        <button
          type="submit"
          disabled={!codigo.trim() || entrando}
          data-testid="entrar-enviar"
          className="rounded-full w-full px-5 py-3 bg-alpha-black text-white font-mono-alpha text-[11px] uppercase tracking-widest disabled:opacity-40"
        >
          Entrar
        </button>
      </form>
    </motion.main>
  );
}
